import { useEffect, useState, type ReactNode } from 'react';
import type { GameState, Phase, SeatId, Warning } from '@risk/engine';
import type { ClientState } from '../net/client.js';
import type { GameClient } from '../net/client.js';
import {
  applyTheme,
  nextTheme,
  resolveTheme,
  storedTheme,
  watchSystemTheme,
  THEME_GLYPH,
  THEME_LABEL,
  type Theme,
} from '../theme.js';

export function SeatDot({ color }: { color: string }): ReactNode {
  return <span className="dot" style={{ background: color }} aria-hidden="true" />;
}

export function seatOf(game: GameState, id: SeatId | null) {
  return id === null ? undefined : game.seats.find((seat) => seat.id === id);
}

/**
 * Cycles system -> light -> dark. Three states, not a two-way switch: most
 * people want it to follow the phone, and a plain toggle gives them no way
 * back to that once they have touched it.
 */
export function ThemeToggle(): ReactNode {
  const [theme, setTheme] = useState<Theme>(() => storedTheme());
  const [, bump] = useState(0);

  // While following the system, re-render when it changes so the glyph stays
  // honest about what is actually on screen.
  useEffect(() => {
    if (theme !== 'system') return undefined;
    return watchSystemTheme(() => bump((n) => n + 1));
  }, [theme]);

  const change = (): void => {
    const next = nextTheme(theme);
    setTheme(next);
    applyTheme(next);
  };

  return (
    <button
      type="button"
      className="themetoggle"
      onClick={change}
      title={`Theme: ${THEME_LABEL[theme]}`}
      aria-label={`Theme: ${THEME_LABEL[theme]}${theme === 'system' ? ` (currently ${resolveTheme(theme)})` : ''}. Tap to change.`}
    >
      <span aria-hidden="true">{THEME_GLYPH[theme]}</span>
    </button>
  );
}

export function StatusBar({ state }: { state: ClientState }): ReactNode {
  const label =
    state.status === 'open' ? 'live'
    : state.status === 'connecting' ? 'connecting'
    : state.status === 'reconnecting' ? 'reconnecting'
    : 'offline';

  const online = state.presence.filter((seat) => seat.connected).length;
  return (
    <div className="statusbar">
      <span className={`state ${state.status}`}>{label}</span>
      {state.roomCode && <span className="mono small muted">room {state.roomCode}</span>}
      <span className="grow" />
      {state.presence.length > 0 && (
        <span className="small muted">{online}/{state.presence.length} here</span>
      )}
      <ThemeToggle />
    </div>
  );
}

export function Notice({ state, client }: { state: ClientState; client: GameClient }): ReactNode {
  if (!state.notice) return null;
  return (
    <div className="notice">
      <span className="grow">{state.notice}</span>
      <button className="small ghost" onClick={() => client.dismissNotice()}>Dismiss</button>
    </div>
  );
}

/**
 * The heart of "warn, never block": the app says what looks wrong, and the
 * player decides. Proceeding is one tap, and the override is recorded.
 */
export function WarningSheet({ state, client }: { state: ClientState; client: GameClient }): ReactNode {
  const warning = state.warning;
  if (!warning) return null;

  return (
    <div className="sheet-scrim" role="dialog" aria-modal="true">
      <div className="sheet">
        <h2>That breaks a rule</h2>
        <div style={{ margin: '10px 0 16px' }}>
          {warning.warnings.map((w: Warning, i: number) => (
            <div className="warnrow" key={`${w.code}-${i}`}>
              <div className={`sev ${w.severity}`}>{w.severity}</div>
              <div>{w.message}</div>
            </div>
          ))}
        </div>
        <p className="small muted" style={{ marginTop: 0 }}>
          The table has the final say. If you meant it, the app will keep score anyway
          and note that the rule was overruled.
        </p>
        <div className="row">
          <button className="grow" onClick={() => client.dismissWarning()}>Cancel</button>
          <button className="grow primary" onClick={() => client.overrideWarning()}>Do it anyway</button>
        </div>
      </div>
    </div>
  );
}

const PHASE_LABELS: ReadonlyArray<readonly [Phase, string]> = [
  ['reinforceTrade', 'Cards'],
  ['reinforcePlace', 'Place'],
  ['attack', 'Attack'],
  ['fortify', 'Fortify'],
  ['cardDraw', 'Card'],
  ['audit', 'Audit'],
];

export function PhaseBar({ game }: { game: GameState }): ReactNode {
  const current = PHASE_LABELS.findIndex(([phase]) => phase === game.phase);
  return (
    <div className="phases" role="list">
      {PHASE_LABELS.map(([phase, label], i) => (
        <span
          key={phase}
          role="listitem"
          className={`step ${phase === game.phase ? 'now' : i < current ? 'done' : ''}`}
        >
          {label}
        </span>
      ))}
      {game.phase === 'occupy' && <span className="step now">Move in</span>}
    </div>
  );
}

export function Stepper({
  value,
  min,
  max,
  onChange,
  label,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (next: number) => void;
  label: string;
}): ReactNode {
  const clamp = (n: number): number => Math.max(min, Math.min(max, n));
  return (
    <div className="stepper">
      <button className="small" aria-label={`one fewer ${label}`} onClick={() => onChange(clamp(value - 1))} disabled={value <= min}>−</button>
      <span className="value mono">{value}</span>
      <button className="small" aria-label={`one more ${label}`} onClick={() => onChange(clamp(value + 1))} disabled={value >= max}>+</button>
    </div>
  );
}

export function BottomBar({ children }: { children: ReactNode }): ReactNode {
  return (
    <div className="bar">
      <div className="inner">{children}</div>
    </div>
  );
}
